import { v4 as uuid } from 'uuid';

import { setSendAnswerFailed, setSendAnswerPending, setSendAnswerSuccess } from './actions';
import { trackBubbleInputSend } from './tracking';
import { IWsAnswerEvent } from './types';
import { GuardError } from '../../errors/GuardError';
import { currentOpenChatIDSelector } from '../../selectors';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { EInternetStatus } from '../internetStatus';

type TSendWsAnswerParams = {
  messageId: string;
  payload: string;
  input?: string;
  analyticsBubbleType?: string;
};

/**
 * Отправка действия по WS
 */
export const sendWsAnswer =
  ({ messageId, payload, input, analyticsBubbleType }: TSendWsAnswerParams): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { telemetry, logger, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const {
      socketStatus,
      internetStatus,
      user: { userId: currentUserId },
    } = getState();

    const chatId = currentOpenChatIDSelector(getState()) || '';

    dispatch(setSendAnswerPending());

    const isSocketIsClose = eventChannel.isChannelClosed() || socketStatus === ERequestApiStatus.Failed;
    const isOffline = internetStatus === EInternetStatus.Offline;

    const uid = uuid();

    if (isSocketIsClose) {
      dispatch(setSocketStatusFailed());
    }

    if (isSocketIsClose || isOffline) {
      clientEvents.set(uid, { type: EWsClientEventType.Answer, status: ERequestApiStatus.Failed });
      dispatch(setSendAnswerFailed());

      telemetry.increment(ETelemetryEvents.ChatWsAnswerSendFail);

      const exception = new GuardError({
        domain: EExceptionDomain.Ws,
        message: 'Ошибка при отправке действия по ws: соединение закрыто',
      });

      logger.warning(exception);

      return;
    }

    const wsAnswer: IWsAnswerEvent = {
      type: EWsClientEventType.Answer,
      uid,
      message: {
        messageId,
        payload,
        ...(input && { input }),
      },
      userId: currentUserId,
    };

    clientEvents.set(uid, {
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Answer,
    });

    try {
      await timeoutPromise(eventChannel.sendAnswer(wsAnswer), 10000, EExceptionDomain.Ws);
      dispatch(setSendAnswerSuccess());
      telemetry.increment(ETelemetryEvents.ChatWsAnswerSendSuccess);

      if (input && analyticsBubbleType) {
        trackBubbleInputSend(chatId, analyticsBubbleType);
      }
    } catch (err) {
      telemetry.increment(ETelemetryEvents.ChatWsAnswerSendFail);

      const exception = getGenericLikeError({
        error: err,
        domain: EExceptionDomain.Ws,
        message: 'Ошибка при отправке действия по ws',
      });

      logger.warning(exception);

      clientEvents.set(uid, { type: EWsClientEventType.Answer, status: ERequestApiStatus.Failed });
      dispatch(setSendAnswerFailed());
    }
  };
